import {
    useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import { JobAlertsContextual, JobAlertsContextualProps, Modal } from "@themuse/design-system";
import {
    triggerSnowplowImpressionEvent,
    triggerSnowplowModalActionEvent,
} from "@themuse/snowplow-js-client";
import { jobAlertsSubscribeRequest } from "utils/api";
import { jobCategoryItems } from "utils/searchFacetItems";
import { JobAlertsSubmissionValues } from "interfaces/api/jobAlerts";
import styles from "./JobAlertsModal.module.scss";

export type JobAlertsModalProps = {
    filterProps: JobAlertsContextualProps["filterProps"];
    isOpen: boolean;
    onSubscribed?: (formData?: Record<string, string | boolean>) => void;
    setIsOpen: (b: boolean) => void;
};

export const JobAlertsModal = ({
    filterProps,
    isOpen,
    onSubscribed = () => {},
    setIsOpen,
}: JobAlertsModalProps) => {
    const contentRef = useRef(null);
    const prevOpen = useRef(isOpen);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);

    const { locationValue, modalActionProps } = useMemo(() => {
        const location = filterProps.location === "Remote / Flexible" ? "Flexible / Remote"
            : (filterProps.location || null);
        return {
            locationValue: location,
            modalActionProps: {
                sp_modal_type: "job_alert",
                sp_modal_action_type: "submit",
                sp_modal_job_category: filterProps.category || null,
                sp_modal_job_location: location,
                sp_modal_newsletter_opt_in: null,
                sp_modal_screen_order: null,
                sp_modal_screen_name: null,
            },
        };
    }, [filterProps.category, filterProps.location]);

    const categoryLabel = useMemo(() => {
        const { category } = filterProps;
        return jobCategoryItems.find((x) => x.value === category)?.label || category;
    }, [filterProps.category]);

    const handleFormSubmit = useCallback<JobAlertsContextualProps["onFormSubmit"]>((formData) => {
        /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
        const { special, ...filters } = filterProps;
        const apiData: JobAlertsSubmissionValues = {
            page_section: "search",
            ...filters,
            location: locationValue,
            ...formData,
        };
        return jobAlertsSubscribeRequest(apiData);
    }, [filterProps, locationValue]);

    const handleFormSuccess = useCallback<JobAlertsContextualProps["onFormSuccess"]>((formData) => {
        setIsSuccess(true);
        onSubscribed(formData);
        triggerSnowplowModalActionEvent({
            ...modalActionProps,
            sp_modal_newsletter_opt_in: formData?.newsletter,
        });
    }, [modalActionProps, onSubscribed]);

    const handleFormError = useCallback(() => {
        triggerSnowplowModalActionEvent({
            ...modalActionProps,
            sp_modal_error_type: "Job Alerts subscription was unsuccessful.",
        });
    }, [modalActionProps]);

    useEffect(() => {
        if (!isOpen && prevOpen.current) {
            triggerSnowplowModalActionEvent({
                ...modalActionProps,
                sp_modal_action_type: "close",
            });
        }

        if (isOpen && !prevOpen.current) {
            triggerSnowplowImpressionEvent(contentRef.current as HTMLElement);
        }

        prevOpen.current = isOpen;
    }, [isOpen, modalActionProps]);

    useEffect(() => {
        if (isSuccess) {
            triggerSnowplowImpressionEvent(contentRef.current as HTMLElement);
        }
    }, [isSuccess]);

    return (
        <Modal
            containerClassName={styles.container}
            contentClassName={styles.content}
            isOpen={isOpen}
            label="Job Alerts"
            setIsOpen={setIsOpen}
        >
            <div
                data-tracking-props={JSON.stringify({
                    sp_impression_component: "modal",
                    sp_impression_modal_type: isSuccess ? "job_alert_success" : "job_alert",
                    sp_impression_position: "pop_up",
                })}
                ref={contentRef}
            >
                <JobAlertsContextual
                    filterProps={{
                        ...filterProps,
                        category: categoryLabel,
                    }}
                    onFormError={handleFormError}
                    onFormSubmit={handleFormSubmit}
                    onFormSuccess={handleFormSuccess}
                />
            </div>
        </Modal>
    );
};

JobAlertsModal.displayName = "JobAlertsModal";
